/*
  Sticky checkout bar. Mirrors the real Add to cart button (availability,
  loading) and submits the real product form, so product-addons.js and Dawn's
  product-form handle the add exactly as a normal click would.
*/
(function () {
  if (customElements.get('sticky-checkout')) return;

  class StickyCheckout extends HTMLElement {
    connectedCallback() {
      // Dawn's scroll-in animation leaves a transform on the product info
      // wrapper, which would pin a fixed bar to it. Live under <body> instead
      // (this re-runs connectedCallback once from there).
      if (this.parentElement !== document.body) {
        document.querySelectorAll('body > sticky-checkout[data-section-id="' + this.dataset.sectionId + '"]').forEach((old) => old.remove());
        document.body.appendChild(this);
        return;
      }
      if (!this.unloadBound) {
        this.unloadBound = true;
        document.addEventListener('shopify:section:unload', (e) => {
          if (e.detail && e.detail.sectionId === this.dataset.sectionId) this.remove();
        });
      }

      this.form = document.getElementById(this.dataset.formId);
      this.mainButton = document.getElementById('ProductSubmitButton-' + this.dataset.sectionId);
      this.priceBox = document.getElementById('price-' + this.dataset.sectionId);
      this.button = this.querySelector('[data-sticky-submit]');
      this.label = this.querySelector('[data-sticky-label]');
      this.price = this.querySelector('[data-sticky-price]');
      if (!this.form || !this.mainButton || !this.button) return;

      this.button.addEventListener('click', () => this.submit());
      this.titleEl = this.querySelector('.mw-sticky__title');
      this.baseTitle = this.titleEl ? this.titleEl.textContent : '';
      // Bundle picked in the Delivery & Get It Sooner selector → say so.
      document.addEventListener('change', (e) => {
        if (e.target && e.target.classList && e.target.classList.contains('mw-sel__input')) this.sync();
      });

      // Show only while the real button is off screen.
      this.buttonOnScreen = true;
      this.io = new IntersectionObserver((entries) => {
        this.buttonOnScreen = entries.some((e) => e.isIntersecting);
        this.refresh();
      }, { threshold: 0.01 });
      this.io.observe(this.mainButton);

      // iOS Safari: while the on-screen keyboard is up (newsletter popup,
      // quantity field) fixed elements drift to mid-screen and follow the
      // scroll. Hide the bar while a text field has focus, and pin it to the
      // visual viewport so it stays on the bottom edge after the keyboard
      // closes or when the page is pinch-zoomed.
      this.keyboard = false;
      const isTextField = (el) =>
        el && el.matches && el.matches('input:not([type="radio"]):not([type="checkbox"]):not([type="button"]):not([type="submit"]):not([type="hidden"]), textarea, select, [contenteditable="true"]');
      document.addEventListener('focusin', (e) => { if (isTextField(e.target)) { this.keyboard = true; this.refresh(); } });
      document.addEventListener('focusout', (e) => { if (isTextField(e.target)) { this.keyboard = false; setTimeout(() => { this.place(); this.refresh(); }, 350); } });
      this.vv = window.visualViewport || null;
      if (this.vv) {
        this.onViewport = () => this.place();
        this.vv.addEventListener('resize', this.onViewport);
        this.vv.addEventListener('scroll', this.onViewport);
      }
      this.place();

      // Dawn re-renders the button and price on variant change; copy them over.
      this.mo = new MutationObserver(() => this.sync());
      this.mo.observe(this.mainButton, { attributes: true, childList: true, subtree: true, characterData: true });
      if (this.priceBox) this.mo.observe(this.priceBox, { childList: true, subtree: true, characterData: true, attributes: true });
      this.sync();
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      if (this.mo) this.mo.disconnect();
      if (this.vv && this.onViewport) {
        this.vv.removeEventListener('resize', this.onViewport);
        this.vv.removeEventListener('scroll', this.onViewport);
      }
      document.body.classList.remove('mw-sticky-on');
    }

    refresh() {
      this.toggle(!this.buttonOnScreen && !this.keyboard);
    }

    toggle(show) {
      this.hidden = false;
      this.classList.toggle('is-visible', show);
      document.body.classList.toggle('mw-sticky-on', show);
    }

    /* Keep the bar on the bottom edge of what is actually visible. With no
       keyboard and no zoom this is a no-op (bottom: 0, full width). */
    place() {
      if (!this.vv) return;
      const gap = Math.round(window.innerHeight - (this.vv.offsetTop + this.vv.height));
      this.style.bottom = gap > 1 ? gap + 'px' : '';
      if (this.vv.scale > 1.01) {
        this.style.left = Math.round(this.vv.offsetLeft) + 'px';
        this.style.width = Math.round(this.vv.width) + 'px';
        this.style.right = 'auto';
      } else {
        this.style.left = '';
        this.style.width = '';
        this.style.right = '';
      }
    }

    sync() {
      const unavailable = this.mainButton.disabled;
      const mainText = (this.mainButton.textContent || '').trim();
      this.button.disabled = unavailable;
      this.label.textContent = unavailable ? (mainText || this.dataset.soldOut) : this.dataset.label;

      // Loading / aria state follows the real button while an add is in flight.
      const busy = this.mainButton.getAttribute('aria-disabled') === 'true';
      this.button.classList.toggle('is-loading', busy && !unavailable);
      this.button.setAttribute('aria-disabled', busy ? 'true' : 'false');

      const picked = document.querySelector('.mw-addon:not([hidden]) .mw-sel__input:checked');
      const card = picked ? picked.closest('.mw-sel__opt') : null;
      if (this.titleEl) {
        const name = card ? (card.querySelector('.mw-sel__title') || {}).textContent : '';
        const extra = card ? (card.querySelector('[data-mw-price-now]') || card.querySelector('.mw-sel__price') || {}).textContent : '';
        this.titleEl.textContent = card && name
          ? this.baseTitle + ' + ' + name.trim() + (extra ? ' (' + extra.trim() + ')' : '')
          : this.baseTitle;
      }

      if (this.priceBox && this.price) {
        const onSale = this.priceBox.querySelector('.price--on-sale');
        const el = onSale
          ? this.priceBox.querySelector('.price__sale .price-item--sale')
          : this.priceBox.querySelector('.price__regular .price-item--regular');
        const text = ((el || this.priceBox.querySelector('.price-item') || {}).textContent || '').trim();
        if (text) this.price.textContent = this.totalText(text, card) || text;
      }
    }

    /* Tree price + add-on price, written in the same format as the page price
       (so currency symbol, code and separators all match). Falls back to the
       page price when the variant or amounts are unknown. */
    totalText(pageText, card) {
      if (!card) return null;
      const addon = parseInt(card.closest('.mw-addon')?.dataset.price || '', 10);
      if (!(addon > 0)) return null;
      let prices = {};
      try { prices = JSON.parse(this.dataset.prices || '{}'); } catch (e) { prices = {}; }
      const idInput = this.form.querySelector('input[name="id"], .product-variant-id');
      const tree = idInput ? prices[String(idInput.value)] : undefined;
      if (!(tree > 0)) return null;
      const m = pageText.match(/\d[\d.,]*\d|\d/);
      if (!m) return null;
      const num = m[0];
      const decSep = /[.,]\d{2}$/.test(num) ? num.slice(-3, -2) : '';
      const thouSep = decSep ? (decSep === '.' ? ',' : '.') : (num.indexOf(',') !== -1 ? ',' : (num.indexOf('.') !== -1 ? '.' : ''));
      const cents = tree + addon;
      const whole = String(Math.floor(cents / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, thouSep);
      const out = decSep ? whole + decSep + String(cents % 100).padStart(2, '0') : whole;
      return pageText.replace(num, out);
    }

    submit() {
      if (this.button.disabled || this.button.getAttribute('aria-disabled') === 'true') return;
      if (typeof this.form.requestSubmit === 'function') {
        this.form.requestSubmit(this.mainButton);
      } else {
        this.mainButton.click();
      }
    }
  }

  customElements.define('sticky-checkout', StickyCheckout);
})();
